import type { DoctorSubstituteDocumentType } from '@omecare/utils';
import { intlFormat } from 'date-fns';
import { ExternalLinkIcon, UploadIcon } from 'lucide-react';
import { type ChangeEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type {
  DoctorSubstituteDocumentItem,
  DoctorSubstituteDocumentsViewModel,
} from './use-doctor-substitute-documents.viewmodel';

/** File-picker filter matching the server-side accepted types. */
const ACCEPTED_FILE_EXTENSIONS = '.pdf,.jpg,.jpeg,.png';

const DOCUMENT_LABEL_KEYS: Record<DoctorSubstituteDocumentType, string> = {
  licence: 'doctor-substitute-documents.type.licence',
  rcp: 'doctor-substitute-documents.type.rcp',
};

/**
 * One supporting document: its label, the uploaded filename and date (or
 * "not uploaded"), a view button and a replace/upload file picker.
 *
 * @param props.item - the document to render
 * @param props.viewModel - the documents card view-model
 * @param props.canView - whether the viewer may open the document
 * @param props.canEdit - whether the viewer may upload a new one
 * @returns the row
 */
export const DoctorSubstituteDocumentRow = ({
  item,
  viewModel,
  canView,
  canEdit,
}: {
  item: DoctorSubstituteDocumentItem;
  viewModel: DoctorSubstituteDocumentsViewModel;
  canView: boolean;
  canEdit: boolean;
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { document } = item;

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) {
      viewModel.api.replace({ type: item.type, file });
    }
  };

  const replaceLabel = document
    ? t('doctor-substitute-documents.replace.button')
    : t('doctor-substitute-documents.upload.button');

  return (
    <li className="flex flex-col gap-3 rounded-lg border px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="font-medium text-sm">
          {t(DOCUMENT_LABEL_KEYS[item.type])}
        </span>
        {document ? (
          <span className="truncate text-muted-foreground text-sm">
            {document.filename} ·{' '}
            {t('doctor-substitute-documents.uploaded-at', {
              date: intlFormat(document.uploadedAt),
            })}
          </span>
        ) : (
          <span className="text-muted-foreground text-sm italic">
            {t('doctor-substitute-documents.missing')}
          </span>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {canView && document && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={viewModel.busy}
            onClick={() => viewModel.api.view(item.type)}
          >
            <ExternalLinkIcon aria-hidden />
            {t('doctor-substitute-documents.view.button')}
          </Button>
        )}
        {canEdit && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED_FILE_EXTENSIONS}
              className="hidden"
              onChange={onFileChange}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={viewModel.busy}
              onClick={() => inputRef.current?.click()}
            >
              <UploadIcon aria-hidden />
              {replaceLabel}
            </Button>
          </>
        )}
      </div>
    </li>
  );
};
